// ============================================================================
// Vehicle status vocabulary — the ONE place it is written down.
//
// Mirrors machine-status.ts deliberately. That file's header says: "VEHICLES
// ARE A DIFFERENT VOCABULARY. vehicles.status keeps its own values and its own
// reader… Do not point one at the other." This is that other vocabulary, moved
// out of use-fleet-data.ts so the list, the card and the add/edit form all read
// one list instead of three.
//
// vehicles.status is free-text `text` with NO CHECK constraint (verified live
// 2026-08-24). Nothing here is enforced by the database. So:
//   - VEHICLE_STATUSES is what a person may PICK.
//   - INACTIVE_VEHICLE_STATUSES is what the app READS as inactive, and it is a
//     SUPERSET of the pickable ones. See the note on it below.
//   - Any value already in the column that is on neither list is rendered
//     VERBATIM, never blanked and never rewritten.
// ============================================================================

import type { ComplianceLevel } from './compliance';

/** What a person may pick. Order is the order they are offered in. */
export const VEHICLE_STATUSES = [
  'Active',
  'In Maintenance',
  'Sold',
  'Retired',
] as const;

export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];

/**
 * What the app reads as inactive — a vehicle we no longer run.
 *
 * ⚠️ RECONCILIATION, 2026-08-24. This list is a SUPERSET of the two states the
 * fleet-lifecycle prompt named (`Sold`, `Retired`). It keeps `Inactive` and
 * `Disposed`, which shipped in `INACTIVE_STATUSES` in use-fleet-data.ts on
 * 2026-07-17 and which this file replaces.
 *
 * Why keep them: dropping a value from a predicate is not a cosmetic edit — it
 * would make any row carrying it reappear in the default fleet view and start
 * counting toward "Active vehicles" again. Zero live rows carry either value
 * today, so keeping them costs nothing and changes nothing; dropping them
 * silently overwrites a shipped constant on the strength of "no rows right
 * now". They are NOT offered in VEHICLE_STATUSES, so nothing new can be written
 * with them and the pair will age out on its own.
 */
export const INACTIVE_VEHICLE_STATUSES = [
  'Sold',
  'Retired',
  'Inactive',
  'Disposed',
] as const;

const INACTIVE = new Set<string>(INACTIVE_VEHICLE_STATUSES);

/** A vehicle we no longer run. NULL/blank is NOT inactive — the assign-id
 *  trigger defaults a blank status to 'Active', and a row that predates it is
 *  an unknown, not a disposal. */
export function isInactiveVehicleStatus(status: string | null | undefined): boolean {
  return INACTIVE.has((status ?? '').trim());
}

/** The complement. This is what the default fleet view and the tiles count. */
export function isActiveVehicleStatus(status: string | null | undefined): boolean {
  return !isInactiveVehicleStatus(status);
}

/**
 * PostgREST filter for "exclude the inactive ones", built FROM the list above
 * so the query and the predicate can never drift apart.
 *
 * The `status.is.null` arm matters: a bare `not.in.(…)` evaluates to NULL for a
 * NULL status and PostgREST drops the row, which would silently hide any
 * pre-trigger vehicle whose status was never set.
 */
export const ACTIVE_VEHICLES_PGRST_FILTER =
  `status.is.null,status.not.in.(${INACTIVE_VEHICLE_STATUSES.join(',')})`;

/** Human label. Unknown/legacy values are shown verbatim so a row like
 *  'Active - Reg Needed' stays visible rather than rendering as blank. */
export function vehicleStatusLabel(status: string | null | undefined): string {
  const s = (status ?? '').trim();
  return s === '' ? '—' : s;
}

/** Reuses the compliance chip palette, exactly as machine statusTone() does,
 *  so vehicles and equipment badge alike. */
export function vehicleStatusTone(status: string | null | undefined): ComplianceLevel {
  const s = (status ?? '').trim();
  if (s === 'Active') return 'current';
  if (s === 'In Maintenance') return 'amber';
  if (isInactiveVehicleStatus(s)) return 'inactive';
  // Anything else is a legacy value still in the column — 'Active - Reg Needed'
  // is the live example. It is a running vehicle with paperwork outstanding, so
  // amber, not danger, and never green.
  return s === '' ? 'missing' : 'amber';
}
